import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Chip,
  TablePagination,
  CircularProgress,
  Typography,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { setLimit, setPage } from "../../../redux/saleReturn/saleReturnSlice";
import { useGetList } from "../../../hooks/useGetList";
import buildParams from "../../../utils/buildParams";
import formatDate from "../../../utils/formatDate";
import { FaCalendarAlt } from "react-icons/fa";

const SaleReturnList = () => {
  const navigate = useNavigate();
  const filter = useSelector((state) => state.saleReturn);
  const dispatch = useDispatch();
  const params = buildParams({ ...filter, keyword: undefined });
  const paramsKey = JSON.stringify(params);
  const lastFetchedKeyRef = useRef(null);

  const { data, loading, fetchData } = useGetList("v1/sale-returns", false);

  useEffect(() => {
    if (paramsKey === lastFetchedKeyRef.current) return;
    lastFetchedKeyRef.current = paramsKey;
    fetchData(
      "v1/sale-returns",
      Object.keys(params).length ? params : undefined,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey]);

  const handleChangePage = (e, page) => {
    dispatch(setPage(page + 1));
  };

  const handleChangeRowsPerPage = (e) => {
    dispatch(setLimit(Number(e.target.value)));
    dispatch(setPage(1));
  };

  const getStatusChip = (status) => {
    const colors = {
      Processed: { bgcolor: "#4caf50", color: "#fff" },
      Pending: { bgcolor: "#ff9800", color: "#fff" },
      Cancelled: { bgcolor: "#f44336", color: "#fff" },
    };
    const style = colors[status] || { bgcolor: "#9e9e9e", color: "#fff" };
    return (
      <Chip
        label={status || "N/A"}
        size="small"
        sx={{ ...style, fontWeight: 500, fontSize: "12px" }}
      />
    );
  };

  const rows = data?.data || data?.list || [];
  const total = data?.pagination?.total || data?.metadata?.total || 0;

  const keyword = (filter.keyword || "").trim().toLowerCase();
  const filteredRows = keyword
    ? rows.filter((row) => {
        const itemNames = (row.items || [])
          .map((item) => item?.name || item?.item_id?.name)
          .filter(Boolean)
          .join(" ");
        const searchable = [
          row.code,
          row.relatedVoucher?.code,
          row.voucherCode,
          row.relatedPatient?.name,
          row.customerName,
          row.relatedPatient?.phone,
          row.customerPhone,
          row.paymentMethod,
          row.paymentType,
          row.refundAccount?.name,
          itemNames,
          row.reason,
          row.status,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return searchable.includes(keyword);
      })
    : rows;

  return (
    <Box sx={{ width: "100%" }}>
      <TableContainer
        component={Paper}
        sx={{ boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}
      >
        <Table sx={{ minWidth: 900 }} size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
              <TableCell sx={{ fontWeight: 600, width: 40 }}>#</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Return Code</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Voucher Code</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Customer</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Return Date</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Items</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Refund Amount</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Payment</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Reason</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      flexDirection: "column",
                      gap: 2,
                    }}
                  >
                    <CircularProgress size={28} sx={{ color: "#1976d2" }} />
                    <Typography variant="body2" color="text.secondary">
                      Loading sale return records...
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : filteredRows.length > 0 ? (
              filteredRows.map((row, index) => (
                <TableRow
                  key={row._id || index}
                  onClick={() => navigate(`/sale-return/${row._id}`)}
                  sx={{
                    cursor: "pointer",
                    "&:hover": { backgroundColor: "#f0f7ff" },
                    "&:last-child td, &:last-child th": { border: 0 },
                  }}
                >
                  <TableCell>
                    {(filter.page - 1) * filter.limit + index + 1}
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 500, fontSize: "13px" }}
                    >
                      {row.code || "N/A"}
                    </Typography>
                    {row.createdAt && (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          mt: 0.3,
                        }}
                      >
                        <FaCalendarAlt size={11} color="#888" />
                        <Typography
                          variant="body2"
                          sx={{ fontSize: "11px", color: "#888" }}
                        >
                          {formatDate(row.createdAt)}
                        </Typography>
                      </Box>
                    )}
                  </TableCell>
                  <TableCell
                    sx={{
                      color: "#1976d2",
                      fontFamily: "monospace",
                      fontSize: "13px",
                    }}
                  >
                    {row.relatedVoucher?.code || row.voucherCode || "N/A"}
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 500, fontSize: "13px" }}
                    >
                      {row.relatedPatient?.name || row.customerName || "N/A"}
                    </Typography>
                    {(row.relatedPatient?.phone || row.customerPhone) && (
                      <Typography
                        variant="body2"
                        sx={{ fontSize: "12px", color: "#666" }}
                      >
                        {row.relatedPatient?.phone || row.customerPhone}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ fontSize: "13px" }}>
                    {row.returnDate ? formatDate(row.returnDate) : "N/A"}
                  </TableCell>
                  <TableCell sx={{ fontSize: "13px" }}>
                    {row.items?.length ? (
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 0.3,
                        }}
                      >
                        {row.items.slice(0, 3).map((item, i) => (
                          <Typography
                            key={i}
                            variant="body2"
                            sx={{ fontSize: "12px", color: "#444" }}
                          >
                            {item?.name || item?.item_id?.name || "N/A"}
                          </Typography>
                        ))}
                        {row.items.length > 3 && (
                          <Typography
                            variant="body2"
                            sx={{ fontSize: "12px", color: "#888" }}
                          >
                            +{row.items.length - 3} more
                          </Typography>
                        )}
                      </Box>
                    ) : (
                      row.itemCount || 0
                    )}
                  </TableCell>
                  <TableCell
                    sx={{ color: "#2e7d32", fontWeight: 500, fontSize: "13px" }}
                  >
                    {(
                      row.totalRefundAmount ||
                      row.totalReturnAmount ||
                      0
                    ).toLocaleString()}
                  </TableCell>
                  <TableCell sx={{ fontSize: "13px" }}>
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 0.3 }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ fontSize: "12px", fontWeight: 500 }}
                      >
                        {row.paymentType || row.paymentMethod || "N/A"}
                      </Typography>
                      {row.refundAccount?.name && (
                        <Typography
                          variant="body2"
                          sx={{ fontSize: "12px", color: "#666" }}
                        >
                          {row.refundAccount.name}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ fontSize: "13px", maxWidth: 160 }}>
                    {row.reason
                      ? row.reason.length > 40
                        ? row.reason.substring(0, 40) + "..."
                        : row.reason
                      : "N/A"}
                  </TableCell>
                  <TableCell>{getStatusChip(row.status)}</TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={10}
                  align="center"
                  sx={{ py: 4, color: "#888" }}
                >
                  No sale return records found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[5, 10, 15, 20, 25, 100]}
        component="div"
        count={total}
        rowsPerPage={filter.limit}
        page={filter.page - 1}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          "& .MuiTablePagination-toolbar": {
            display: "flex",
            alignItems: "center",
            minHeight: "52px",
          },
          "& .MuiTablePagination-selectLabel": {
            margin: 0,
            display: "flex",
            alignItems: "center",
          },
          "& .MuiTablePagination-displayedRows": {
            margin: 0,
            display: "flex",
            alignItems: "center",
          },
          "& .MuiTablePagination-actions": {
            display: "flex",
            alignItems: "center",
          },
        }}
      />
    </Box>
  );
};

export default SaleReturnList;